import { useEffect, useRef, useState } from "react";
import Reveal from "@Components/Reveal/Reveal";
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import styles from "./Ambassadors.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images and animations come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ScholarshipV2");

// Animated marks, one per criterion. The static line icons these replaced were
// borrowed wholesale from the Categories section as placeholders — which meant this
// band ALSO carried Categories' shuffle, showing a globe against "A clear dream", a
// rocket against "A real reason" and a graduation cap against "Proof you have
// started". Each mark now argues for the criterion it sits under:
//
//   eye     — "we should be able to PICTURE what you are chasing"
//   heart   — "why it MATTERS to you"
//   flag    — a first marker planted; "early work, small wins, first tries"
//   speech  — a bubble with words in it; "whether your STORY lands"
//
// The four static icon-*.svg files are still in ../../assets and still used nowhere
// else, so this band no longer pins them.
//
// ⚠️  speech.json IS A MODIFIED COPY (Iconly animation 398). Iconly's whole Message
// category is Two-Tone: it carries a secondary layer at 40% opacity that renders
// grey, which would have made it the only greyed mark in a set of four solid ones.
// That layer was lifted to 100%, which is the entire diff — the same treatment
// trophy.json needed in Categories. Re-exporting from the MCP brings the grey back.
const lottieEye = asset("ambassadors-eye.json");
const lottieHeart = asset("ambassadors-heart.json");
const lottieFlag = asset("ambassadors-flag.json");
const lottieSpeech = asset("ambassadors-speech.json");

/**
 * `scale` equalises APPARENT size across four very different silhouettes.
 *
 * Every comp is square, but these shapes are not: the eye is wide and flat
 * (156x126 measured), the flag tall and narrow (123x162), the heart and bubble
 * roughly square. Normalising on HEIGHT — which is what the Categories row does,
 * where all four marks are near-square — would blow the eye up to 182px wide
 * against the heart's 162 and make it the loudest thing in the band.
 *
 * So these are normalised on the geometric mean of the ink instead, sqrt(w*h),
 * which is the honest stand-in for visual area when aspect ratios differ:
 *
 *   eye     156.0 x 126.0  ->  mean 140.2  ->  148/140.2 = 1.06
 *   heart   162.5 x 150.5  ->  mean 156.4  ->  148/156.4 = 0.95
 *   flag    123.0 x 162.5  ->  mean 141.4  ->  148/141.4 = 1.05
 *   speech  155.8 x 150.2  ->  mean 153.0  ->  148/153.0 = 0.97
 *
 * At the 56px slot that lands every mark's ink around 44px, which is where the
 * static glyphs this replaced sat (40-49px) — so the band keeps its weight.
 */
const PERKS = [
  {
    lottie: lottieEye,
    scale: 1.06,
    title: "scholarship.v2.judged.item1.title",
    description: "scholarship.v2.judged.item1.description",
  },
  {
    lottie: lottieHeart,
    scale: 0.95,
    title: "scholarship.v2.judged.item2.title",
    description: "scholarship.v2.judged.item2.description",
  },
  {
    lottie: lottieFlag,
    scale: 1.05,
    title: "scholarship.v2.judged.item3.title",
    description: "scholarship.v2.judged.item3.description",
  },
  {
    lottie: lottieSpeech,
    scale: 0.97,
    title: "scholarship.v2.judged.item4.title",
    description: "scholarship.v2.judged.item4.description",
  },
];

/** Gap between one column's one-shot and the next, in ms. */
const STAGGER = 140;

/**
 * The slot each mark is drawn into, and therefore the mark's size.
 *
 * 56 -> 48: the marks read too heavy against an 18px title. Both the box and
 * `.iconSlot` carry this number, so the column tightens by the same 8px rather
 * than leaving the mark rattling around inside a slot it no longer fills.
 *
 * The per-icon `scale` values are untouched — they encode the balance BETWEEN the
 * four, so shrinking the box shrinks all four by exactly the same proportion and
 * the set stays matched.
 */
const ICON_BOX = 48;

/** What the panel scores an application on — Figma nodes 2118:3824 and 2118:3888. */
const Ambassadors = () => {
  const { t } = useTranslationHook();
  /** Column whose mark should be replaying: a hovered one, or one the stagger reached. */
  const [activeCol, setActiveCol] = useState<number | null>(null);
  /** The first column — the band is "arrived" once that is on screen. */
  const gridRef = useRef<HTMLDivElement>(null);

  /**
   * Play each mark once as the band arrives, left to right.
   *
   * InlineLottie parks on its finished frame and replays on hover. On its own that
   * would leave this band completely still on touch, where there is no hover at
   * all — the marks would animate for mouse users and never for anyone on a phone.
   * The one-shot below fires once, when the grid first reaches the viewport, and
   * then hover takes over. Same arrangement as the Categories list.
   */
  useEffect(() => {
    const node = gridRef.current;
    if (!node || typeof IntersectionObserver !== "function") return undefined;

    const timers: number[] = [];
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        PERKS.forEach((_, i) => {
          timers.push(
            window.setTimeout(() => setActiveCol(i), i * STAGGER),
            // Released again so a later hover is a real change of `play` rather than
            // a no-op, and so no column stays latched as "playing".
            window.setTimeout(() => setActiveCol(null), i * STAGGER + STAGGER - 10),
          );
        });
      },
      { threshold: 0.3 },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      timers.forEach(window.clearTimeout);
    };
  }, []);

  return (
    <section className={styles.section}>
      {/* Edge-to-edge divider from the steps section above. */}
      <span className={styles.topRule} aria-hidden="true" />

      <div className={styles.inner}>
        <Reveal as="h2" className={styles.heading}>
          {t("scholarship.v2.judged.heading")}
          <br />
          <span className={styles.headingMuted}>{t("scholarship.v2.judged.headingMuted")}</span>
        </Reveal>

        <Reveal className={styles.grid} delay={100}>
          {PERKS.map((perk, i) => (
            <div
              key={perk.title}
              // The observer watches the FIRST column rather than a probe element of
              // its own: the grid's children are its cells, so an extra div here
              // would take a fifth cell and break the four-across layout.
              ref={i === 0 ? gridRef : undefined}
              className={styles.perk}
              onMouseEnter={() => setActiveCol(i)}
              onMouseLeave={() => setActiveCol(null)}
            >
              {/* Shared 56px slot; the mark is centred in it and magnified by
                  transform, so its apparent size costs no layout. Hovering the whole
                  COLUMN replays it rather than the 56px mark alone, which would be a
                  far smaller target than the card it sits in. */}
              <span className={styles.iconSlot} aria-hidden="true">
                <InlineLottie
                  src={perk.lottie}
                  size={ICON_BOX}
                  scale={perk.scale}
                  autoPlay={false}
                  play={activeCol === i}
                />
              </span>
              <div className={styles.perkText}>
                <span className={styles.perkTitle}>{t(perk.title)}</span>
                <span className={styles.perkDesc}>{t(perk.description)}</span>
              </div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

export default wrapperHOC(Ambassadors, {
  componentName: "Ambassadors-ScholarshipV2",
  showForChina: true,
});
